import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { colors, glassTextures, glazingTypes, woodJointOptions, profileSystems } from '../data/mockData';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import ProfileSystemPicker from './ProfileSystemPicker';
import { useTranslation } from '../context/TranslationContext';
import {
  getDoorMinTotalWidth,
  DOOR_MAX_TOTAL_WIDTH,
  DOOR_MIN_DOOR_HEIGHT_MM,
  DOOR_MAX_TOTAL_HEIGHT,
} from '../lib/voordeurConfigHelpers';

const VoordeurOptionsPanel = ({
  config,
  onConfigChange,
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();
  
  // Helper function to translate glass texture and glazing type names
  const translateGlassTextureName = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato')
    };
    return translations[id] || id;
  };
  
  const translateGlassTextureDescription = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlassDesc'),
      'chinchilla': t('options.chinchillaDesc'),
      'satinato': t('options.satinatoDesc')
    };
    return translations[id] || '';
  };
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeDescription = (id) => {
    const translations = {
      'double': t('options.doubleGlass24Desc'),
      'triple': t('options.doubleGlass36Desc'),
      'double-24': t('options.doubleGlass24Desc'),
      'double-36': t('options.doubleGlass36Desc')
    };
    return translations[id] || '';
  };

  const translateWoodJointName = (id) => {
    const translations = {
      'joint-45': t('options.woodJoint45'),
      'joint-90': t('options.woodJoint90')
    };
    return translations[id] || id;
  };

  const doorStyles = [
    { id: 'style-1', name: t('options.style1') },
    { id: 'style-2', name: t('options.style2') },
    { id: 'style-3', name: t('options.style3') },
    { id: 'style-4', name: t('options.style4') },
    { id: 'style-5', name: t('options.style5') },
    { id: 'style-6', name: t('options.style6') }
  ];

  const designOptions = [
    { id: 'no-panel', name: t('options.noPanel'), description: t('options.noPanelDesc'), image: process.env.PUBLIC_URL + '/images/front%20door/no-panel.png' },
    { id: 'left-panel', name: t('options.leftPanel'), description: t('options.leftPanelDesc'), image: process.env.PUBLIC_URL + '/images/front%20door/Left-panel.png' },
    { id: 'right-panel', name: t('options.rightPanel'), description: t('options.rightPanelDesc'), image: process.env.PUBLIC_URL + '/images/front%20door/right-panel.png' },
    { id: 'both-panels', name: t('options.bothPanels'), description: t('options.bothPanelsDesc'), image: process.env.PUBLIC_URL + '/images/front%20door/both-panel.png' }
  ];

  const doorTypes = [
    { id: 'normal', name: t('options.normal') },
    { id: 'top-light', name: t('options.topLight') }
  ];

  const numberOfPartsOptions = [
    { id: 3, name: t('options.parts3') },
    { id: 4, name: t('options.parts4') }
  ];

  const thresholdHeights = [
    { id: '30mm', name: '30 mm', value: 30 },
    { id: '55mm', name: '55 mm', value: 55 }
  ];

  const handlePositions = [
    { id: 'left', name: t('options.doorHandleInsideLeft'), image: process.env.PUBLIC_URL + '/images/klink-links-151x300.png' },
    { id: 'right', name: t('options.doorHandleInsideRight'), image: process.env.PUBLIC_URL + '/images/klink-rechts-151x300.png' }
  ];

  const parapetOptions = [
    { id: 'standard-knob', name: t('options.standardDoorKnob'), image: process.env.PUBLIC_URL + '/images/zsg-8-breed-thumbnail-150x150.png' },
    { id: 'rvs-bar', name: t('options.rvsBar'), image: process.env.PUBLIC_URL + '/images/RVSthumbnail.png' }
  ];

  const [widthInput, setWidthInput] = useState(String(config.width));
  const [heightInput, setHeightInput] = useState(String(config.height));

  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);

  const commitDimension = (field, rawValue, min, max) => {
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    onConfigChange(field, clamped);
  };

  const minWidth = getDoorMinTotalWidth(config.designType);
  const maxWidth = DOOR_MAX_TOTAL_WIDTH;
  const minHeight = DOOR_MIN_DOOR_HEIGHT_MM;
  const maxHeight = DOOR_MAX_TOTAL_HEIGHT;

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden p-4 md:p-5 xl:p-6 bg-white border-r border-gray-200 touch-manipulation">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('options.frontDoorConfiguration')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('options.configureFrontDoor')}</p>
        </div>

        <ProfileSystemPicker
          profileSystems={profileSystems}
          config={config}
          onConfigChange={onConfigChange}
          translateName={(id, name) => ({ schuco: t('options.schuco'), synego: t('options.synego') }[id] || name)}
          translateDescription={(id, description) => ({ schuco: t('options.schucoDesc'), synego: t('options.synegoDesc') }[id] || description)}
        />

        {/* Wood Joint */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.woodJoint')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {woodJointOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onConfigChange('woodJoint', option.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    config.woodJoint === option.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    config.woodJoint === option.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>
                    {translateWoodJointName(option.id)}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Glazing Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glazingType')}</CardTitle>
            <CardDescription className="text-xs">{t('options.selectGlazingType')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glazingTypes.map(glazing => (
                <button
                  key={glazing.id}
                  onClick={() => onConfigChange('glazingType', glazing.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.glazingType === glazing.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {glazing.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={glazing.image}
                        alt={glazing.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.glazingType === glazing.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlazingTypeName(glazing.id)}
                    </div>
                    {glazing.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlazingTypeDescription(glazing.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Door Style */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.doorStyle')}</CardTitle>
            <CardDescription className="text-xs">{t('options.indicateWhichDesign')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {doorStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => onConfigChange('doorStyle', style.id)}
                  className={`relative rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 overflow-hidden ${
                    config.doorStyle === style.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full aspect-square flex items-center justify-center p-2 ${
                    config.doorStyle === style.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img
                      src={process.env.PUBLIC_URL + `/images/${style.id}.png`}
                      alt={style.name}
                      className="max-h-full max-w-full object-contain w-full h-full"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className={`text-xs font-semibold text-center py-2 px-1 ${
                    config.doorStyle === style.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{style.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Design Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.designSettings')}</CardTitle>
            <CardDescription className="text-xs">{t('options.chooseSidePanelConfiguration')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {designOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onConfigChange('designType', option.id)}
                  className={`relative rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 overflow-hidden ${
                    config.designType === option.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full aspect-video flex items-center justify-center p-2 ${
                    config.designType === option.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img
                      src={option.image}
                      alt={option.name}
                      className="max-h-full max-w-full object-contain w-full h-full"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className={`text-xs font-semibold text-center py-2 px-1 ${
                    config.designType === option.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{option.name}</div>
                  {option.description && (
                    <div className="text-[10px] text-gray-500 text-center px-1 pb-2">{option.description}</div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Specifications */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.specifications')}</CardTitle>
            <CardDescription className="text-xs">{t('options.setDimensions')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="door-width" className="text-sm font-medium">{t('options.width')}</Label>
                <Input
                  id="door-width"
                  type="number"
                  value={widthInput}
                  onChange={(e) => setWidthInput(e.target.value)}
                  onBlur={() => commitDimension('width', widthInput, minWidth, maxWidth)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('width', widthInput, minWidth, maxWidth); }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="door-height" className="text-sm font-medium">{t('options.height')}</Label>
                <Input
                  id="door-height"
                  type="number"
                  value={heightInput}
                  onChange={(e) => setHeightInput(e.target.value)}
                  onBlur={() => commitDimension('height', heightInput, minHeight, maxHeight)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('height', heightInput, minHeight, maxHeight); }}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">{t('options.doorType')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {doorTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => onConfigChange('doorType', type.id)}
                    className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                      config.doorType === type.id
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`text-sm font-medium ${
                      config.doorType === type.id ? 'text-orange-600' : 'text-gray-900'
                    }`}>{type.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {(config.doorStyle === 'style-1' || config.doorStyle === 'style-6') && (
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('options.numberOfParts')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {numberOfPartsOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => onConfigChange('numberOfParts', option.id)}
                      className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                        (config.numberOfParts || 3) === option.id
                          ? 'border-orange-500 bg-orange-50 shadow-sm'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`text-sm font-medium ${
                        (config.numberOfParts || 3) === option.id ? 'text-orange-600' : 'text-gray-900'
                      }`}>{option.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Overhead Parts - only show when Top Light is selected */}
            {config.doorType === 'top-light' && (
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('options.overheadParts')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      onConfigChange('overheadParts', 1);
                      onConfigChange('overheadPartWindowTypes', ['fixed-sash']);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                      (config.overheadParts || 1) === 1
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`text-sm font-medium ${
                      (config.overheadParts || 1) === 1 ? 'text-orange-600' : 'text-gray-900'
                    }`}>1 Part</div>
                  </button>
                  <button
                    onClick={() => {
                      onConfigChange('overheadParts', 2);
                      onConfigChange('overheadPartWindowTypes', ['fixed-sash', 'fixed-sash']);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                      (config.overheadParts || 1) === 2
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`text-sm font-medium ${
                      (config.overheadParts || 1) === 2 ? 'text-orange-600' : 'text-gray-900'
                    }`}>2 Parts</div>
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Threshold */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.threshold')}</CardTitle>
            <CardDescription className="text-xs">{t('options.setThresholdHeight')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {thresholdHeights.map((height) => (
                <button
                  key={height.id}
                  onClick={() => onConfigChange('thresholdHeight', height.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    config.thresholdHeight === height.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    config.thresholdHeight === height.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{height.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Door Handle */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.handle')}</CardTitle>
            <CardDescription className="text-xs">Choose handle position</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {handlePositions.map((position) => (
                <button
                  key={position.id}
                  onClick={() => onConfigChange('handlePosition', position.id)}
                  className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                    config.handlePosition === position.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full h-32 mb-2 rounded flex items-center justify-center p-2 ${
                    config.handlePosition === position.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    {position.image && (
                      <img
                        src={position.image}
                        alt={position.name}
                        className="max-h-full max-w-full object-contain"
                        style={{
                          display: 'block',
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <div
                    className={`text-xs font-medium text-center ${
                      config.handlePosition === position.id ? 'text-orange-600' : 'text-gray-900'
                    }`}
                  >
                    {position.name}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Parapet */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.parapet')}</CardTitle>
            <CardDescription className="text-xs">Do you want to apply so-called parapet for this front door?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {parapetOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onConfigChange('parapet', option.id)}
                  className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                    config.parapet === option.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full h-24 mb-2 rounded flex items-center justify-center p-2 ${
                    config.parapet === option.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    {option.image && (
                      <img
                        src={option.image}
                        alt={option.name}
                        className="max-h-full max-w-full object-contain"
                        style={{
                          display: 'block',
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <div className={`text-xs font-semibold text-center ${
                    config.parapet === option.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{option.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Glass Texture */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glassTexture')}</CardTitle>
            <CardDescription className="text-xs">Select glass texture</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glassTextures.map(texture => (
                <button
                  key={texture.id}
                  onClick={() => onConfigChange('glassTexture', texture.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.glassTexture === texture.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {texture.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={texture.image}
                        alt={texture.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.glassTexture === texture.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlassTextureName(texture.id)}
                    </div>
                    {texture.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlassTextureDescription(texture.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Interior Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.interiorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.interiorColor}
              onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>

        {/* Exterior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.exteriorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.exteriorColor}
              onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>
        {onAdditionalItemsChange ? (
          <ConfiguratorAdditionalItems
            items={additionalItems}
            onItemsChange={onAdditionalItemsChange}
          />
        ) : null}
      </div>
    </div>
  );
};

export default VoordeurOptionsPanel;

